import type {
  CouponData,
  PermanentDiscountData,
  PremiumInterval,
  PriceData,
  PriceEntry,
  ReferralDiscountData,
} from '@flows-ui/services/QuoteDataContext.types'

export const REFERRAL_CREDIT_AMOUNT = 15

export const getReferralCreditForInterval = (premiumInterval: PremiumInterval): number =>
  premiumInterval === 'P1M' ? REFERRAL_CREDIT_AMOUNT / 12 : REFERRAL_CREDIT_AMOUNT

export const findPriceEntry = ({
  premiumInterval,
  priceData,
  productKey,
  tariffLine,
  configurationId,
}: {
  premiumInterval: string
  priceData: PriceData | undefined
  productKey: string
  tariffLine?: string
  configurationId?: string
}): PriceEntry | undefined => {
  const productConfigurations = (priceData?.product_configurations ?? []).filter(
    (configuration) => configuration.product_key === productKey
  )
  const exactIdMatch = configurationId
    ? productConfigurations.find((c) => c.configuration_id === configurationId)
    : undefined
  const tariffLineMatch = tariffLine
    ? productConfigurations.find((c) => c.configuration_id?.includes(tariffLine))
    : undefined
  const configuration = exactIdMatch ?? tariffLineMatch ?? productConfigurations[0]

  return configuration?.prices.find((price) => price.premium_interval === premiumInterval)
}

export const getPriceAfterDiscount = ({
  price,
  discountAmount,
}: {
  price: number
  discountAmount: number | null | undefined
}): number => Math.max(0, price - (discountAmount ?? 0))

export type AppliedDiscountType = 'coupon' | 'permanent' | 'referral' | 'none'

export type AppliedDiscount = {
  type: AppliedDiscountType
  amount: number
  finalPrice: number
}

export const resolveAppliedDiscount = ({
  couponInfo,
  grossPremium,
  permanentDiscount,
  referralDiscount,
}: {
  couponInfo: CouponData | null | undefined
  grossPremium: number
  permanentDiscount: PermanentDiscountData | null | undefined
  referralDiscount: ReferralDiscountData | null | undefined
}): AppliedDiscount => {
  if (couponInfo && couponInfo.amount > 0) {
    return {
      type: 'coupon',
      amount: couponInfo.amount,
      finalPrice: getPriceAfterDiscount({ price: grossPremium, discountAmount: couponInfo.amount }),
    }
  }

  if (permanentDiscount?.shown) {
    return {
      type: 'permanent',
      amount: permanentDiscount.amount ?? 0,
      finalPrice: permanentDiscount.grossPremiumAfterDiscount,
    }
  }

  if (referralDiscount?.shouldShowReferralDiscount) {
    return {
      type: 'referral',
      amount: referralDiscount.referralCreditToggledAmount,
      finalPrice: getPriceAfterDiscount({
        price: grossPremium,
        discountAmount: referralDiscount.referralCreditToggledAmount,
      }),
    }
  }

  return { type: 'none', amount: 0, finalPrice: grossPremium }
}

export const getDiscountPercent = ({
  amount,
  grossPremium,
}: {
  amount: number
  grossPremium: number
}): number => (grossPremium > 0 ? Math.round((amount / grossPremium) * 100) : 0)
